import { ReactNode, useMemo } from 'react';
import { css } from '@emotion/react';
import { theme } from '@frontend/theme';
import { Stepper, useResponsiveModal } from '@frontend/design-system';

export type StepperInfo = { id: string; title: string };
export type StepStatus = 'completed' | 'active' | 'inactive' | 'currActive';

const styles = {
  stepper: css`
    margin-left: ${theme.spacing(4)};
    margin-top: ${theme.spacing(-1)};
  `,
  stepperCard: css`
    border: none;
    margin-left: ${theme.spacing(2)};
    padding: ${theme.spacing(1)};
    padding-left: ${theme.spacing(2)};
    align-items: center;
    display: flex;
  `,
  stepperTitle: css`
    & h2.step-content-title {
      font-size: ${theme.font.size.large};
      width: 100%;
      white-space: normal;
      overflow: visible;
      height: auto;
    }
  `,
  modalContent: (stepper: StepperInfo[] | undefined, isResponsiveMode = false) => [
    css`
      flex: 1;
      overflow-y: auto;
    `,
    isResponsiveMode
      ? css`
          display: flex;
          flex-direction: column;
        `
      : stepper &&
        css`
          border-left: 1px solid ${theme.colors.neutral60};
          padding-top: ${theme.spacing(3)};
          padding-bottom: ${theme.spacing(3)};
          margin-top: ${theme.spacing(-3)};
          margin-bottom: ${theme.spacing(-3)};
        `,
  ],
};

export const MultiStepModalStepper = ({
  children,
  steps,
  uniqueStepperSteps,
  stepperWidth,
  stepper,
}: {
  children?: ReactNode;
  steps: string[];
  uniqueStepperSteps: Record<string, string[]> | undefined;
  stepper: StepperInfo[] | undefined;
  stepperWidth?: number;
}) => {
  const { isResponsiveMode } = useResponsiveModal();

  const isCompletedStep = (stepTitle: string) => {
    const uniqueSteps = uniqueStepperSteps?.[stepTitle] ?? [];
    const lastStep = steps[steps.length - 1];
    return (
      !!uniqueStepperSteps &&
      uniqueSteps.some((stepId) => steps.includes(stepId)) &&
      !uniqueSteps.includes(lastStep ?? '')
    );
  };

  const isActiveStep = (stepTitle: string) => {
    const uniqueSteps = uniqueStepperSteps?.[stepTitle] ?? [];
    const lastStep = steps[steps.length - 1];

    return !!uniqueStepperSteps && uniqueSteps.includes(lastStep ?? '');
  };

  const stepStatus = useMemo(
    () =>
      Object.keys(uniqueStepperSteps ?? {}).reduce(
        (final, stepTitle, index) =>
          ({
            ...final,
            [index + 1]: isCompletedStep(stepTitle) ? 'completed' : isActiveStep(stepTitle) ? 'active' : 'inactive',
          } as Record<number, StepStatus>),
        {} as Record<number, StepStatus>
      ),
    [uniqueStepperSteps, steps]
  );

  return (
    <>
      {!isResponsiveMode && stepper && uniqueStepperSteps && (
        <Stepper
          css={styles.stepper}
          maxWidth={stepperWidth ?? parseFloat(theme.spacing(17.5))}
          stepStatus={stepStatus}
        >
          {Object.keys(uniqueStepperSteps).map((stepTitle) => (
            <Stepper.Card css={styles.stepperCard} key={stepTitle} preventDefaultOnClick>
              <Stepper.Title css={styles.stepperTitle}>{stepTitle}</Stepper.Title>
            </Stepper.Card>
          ))}
        </Stepper>
      )}
      {stepper ? <div css={styles.modalContent(stepper, isResponsiveMode)}>{children}</div> : children}
    </>
  );
};
